import React, { useContext, useEffect, useMemo, useState } from 'react';

import infoTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import Tooltip from '@amzn/meridian/tooltip';

import DatePicker from 'react-datepicker';
import { AppContext } from 'src/contexts/appContext';
import { apiHooks } from 'src/hooks/apiHooks';
import 'src/styles/react-datepicker.css';

import { BaseAgGrid } from '../BaseAgGrid/BaseAgGrid';
import { dateToString, dateToYYYYMM, getMonthsAgoDate } from '../MonthRangePicker/utils';
import './MonthOverMonthPayments.scss';
import {
    HIGHLIGHT_THRESHOLDS,
    getRowHighlightLevel,
    monthOverMonthPaymentsColDefs
} from './monthOverMonthPaymentsColDefs';

const validateMonths = (curr: Date | null, prev: Date | null): boolean => {
    if (!curr || !prev) return false;
    const currStr = dateToString(curr);
    const prevStr = dateToString(prev);
    const nowStr = dateToString(new Date());
    if (currStr > nowStr || prevStr > nowStr) return false;
    return currStr > prevStr;
};

const ALLOWED_SERVICES = ['Unlimited', 'Prime', 'Free'];

interface HighlightsSummary {
    red: { count: number; territories: string[] };
    yellow: { count: number; territories: string[] };
}

const computeHighlightsSummary = (payments: any[]): HighlightsSummary => {
    const summary: HighlightsSummary = {
        red: { count: 0, territories: [] },
        yellow: { count: 0, territories: [] }
    };

    payments.forEach((p) => {
        const level = getRowHighlightLevel(p);
        if (level === 'red') {
            summary.red.count++;
            summary.red.territories.push(p.territoryCode);
        } else if (level === 'yellow') {
            summary.yellow.count++;
            summary.yellow.territories.push(p.territoryCode);
        }
    });

    return summary;
};

export const MonthOverMonthPayments: React.FC = () => {
    const { setTopAlertMsg } = useContext(AppContext);

    const [currMonthDate, setCurrMonthDate] = useState<Date | null>(getMonthsAgoDate(1));
    const [prevMonthDate, setPrevMonthDate] = useState<Date | null>(getMonthsAgoDate(2));
    const [serviceName, setServiceName] = useState('Prime');
    const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

    const [apiPayload, setApiPayload] = useState({
        currYearMonth: dateToYYYYMM(getMonthsAgoDate(1)),
        prevYearMonth: dateToYYYYMM(getMonthsAgoDate(2)),
        serviceName: 'Prime'
    });

    const { response: servicesResponse } = apiHooks.useApiCall('getServicesByCode', {});
    const filteredServices =
        servicesResponse?.services?.filter((s) => ALLOWED_SERVICES.includes(s.serviceName || '')) || [];

    const { response, loading, error } = apiHooks.useApiCall('getMonthOverMonthPayments', apiPayload);

    const payments = useMemo(() => {
        const data = (response?.payments || []) as any[];
        if (showFlaggedOnly) {
            return data.filter((p) => getRowHighlightLevel(p) !== 'none');
        }
        return data;
    }, [response?.payments, showFlaggedOnly]);

    const highlightsSummary = useMemo(() => computeHighlightsSummary(payments as any[]), [payments]);

    useEffect(() => {
        if (error) {
            setTopAlertMsg('Failed to load payment data');
        }
    }, [error, setTopAlertMsg]);

    const handleLoadData = () => {
        if (validateMonths(currMonthDate, prevMonthDate) && serviceName) {
            setApiPayload({
                currYearMonth: dateToYYYYMM(currMonthDate),
                prevYearMonth: dateToYYYYMM(prevMonthDate),
                serviceName
            });
        } else {
            setTopAlertMsg('Please enter valid months (current > previous, no future months)');
        }
    };

    const isLoadButtonDisabled = loading || !serviceName;

    return (
        <Column className="month-over-month-payments" width="100%" height="100%" spacing="300">
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Heading level={1}>Month Over Month Payments Comparison</Heading>
                    <div className="highlights-summary">
                        {highlightsSummary.red.count > 0 && (
                            <div
                                className="highlight-badge red"
                                title={`Territories: ${highlightsSummary.red.territories.join(', ')}`}
                            >
                                <span>{highlightsSummary.red.count} Critical</span>
                                <Tooltip
                                    position="bottom"
                                    title={`Diff ≥ $${(HIGHLIGHT_THRESHOLDS.DIFF_USD / 1000).toFixed(0)}k AND Change ≥ ${HIGHLIGHT_THRESHOLDS.CHANGE_PERCENT}%`}
                                >
                                    <Icon tokens={infoTokens} />
                                </Tooltip>
                            </div>
                        )}
                        {highlightsSummary.yellow.count > 0 && (
                            <div
                                className="highlight-badge yellow"
                                title={`Territories: ${highlightsSummary.yellow.territories.join(', ')}`}
                            >
                                <span>{highlightsSummary.yellow.count} Warning</span>
                                <Tooltip
                                    position="bottom"
                                    title={`Diff ≥ $${(HIGHLIGHT_THRESHOLDS.DIFF_USD / 1000).toFixed(0)}k OR Change ≥ ${HIGHLIGHT_THRESHOLDS.CHANGE_PERCENT}%`}
                                >
                                    <Icon tokens={infoTokens} />
                                </Tooltip>
                            </div>
                        )}
                    </div>
                </Row>

                <Row spacing="500" alignmentVertical="bottom">
                    <Column spacing="200">
                        <Text>Previous Month</Text>
                        <DatePicker
                            className="month-filter-input"
                            selected={prevMonthDate}
                            onChange={setPrevMonthDate}
                            dateFormat="yyyy-MM"
                            showMonthYearPicker
                            placeholderText="YYYY-MM"
                            maxDate={new Date()}
                        />
                    </Column>
                    <Column spacing="200">
                        <Text>Current Month</Text>
                        <DatePicker
                            className="month-filter-input"
                            selected={currMonthDate}
                            onChange={setCurrMonthDate}
                            dateFormat="yyyy-MM"
                            showMonthYearPicker
                            placeholderText="YYYY-MM"
                            maxDate={new Date()}
                        />
                    </Column>
                    <Select label="Service" value={serviceName} onChange={setServiceName}>
                        {filteredServices.map((s) => (
                            <SelectOption key={s.serviceName} value={s.serviceName || ''} label={s.serviceName || ''} />
                        ))}
                    </Select>
                    <Button className="load-data-btn" onClick={handleLoadData} disabled={isLoadButtonDisabled}>
                        {loading ? 'Loading...' : 'Load Data'}
                    </Button>
                    <Toggle checked={showFlaggedOnly} onChange={setShowFlaggedOnly}>
                        Show Flagged Only
                    </Toggle>
                </Row>
            </Box>
            <div className="month-over-month-grid">
                {!loading && payments.length === 0 ? (
                    <Box spacingInset="400">
                        No data available for selected criteria. Try selecting different months or service.
                    </Box>
                ) : (
                    <BaseAgGrid rowData={payments} columnDefs={monthOverMonthPaymentsColDefs} loading={loading} />
                )}
            </div>
        </Column>
    );
};
